import React from 'react';
import { purchaseControlFor, submissionKey, priceLabel, cadenceLabel, taxNote, PURCHASE_MODES } from '../../lib/purchaseMode';

// GOV13 Step 5 Slice 4 — presentation-only purchase control. It receives the ALREADY-resolved backend
// purchase-mode envelope + the caller role + the in-flight submission key, and renders the ONE correct control:
//   DIRECT_CHECKOUT   → Purchase (price + cadence + tax note)
//   REQUEST_REQUIRED  → Request review (+ canonical reason)   [exceptional products only]
//   ALREADY_ACTIVE    → "Already active"  (no button)
//   INCLUDED_IN_PLAN  → "Included in plan" (no button)
//   INELIGIBLE / UNAVAILABLE → exact reason (no button)
// It performs NO fetch/auth, makes NO purchase-mode inference, and creates NO optimistic entitlement. A normal
// product NEVER shows a generic Request button; an exceptional product NEVER shows Purchase. A non-purchaser role
// sees product state + price but NO enabled mutation control (backend remains authoritative).
export default function ProductPurchaseControl({ env, role, canPurchase = undefined, busyKey = null, onPurchase, onRequestReview, quantityControl = null, actionLabel = 'Purchase' }) {
  if (!env) return null;
  const desc = purchaseControlFor(env, role);
  // A parent may pass the authoritative `purchase_controls_enabled` boolean from the server projection; it
  // overrides the local role gate for the ENABLED state only (backend routes remain authoritative regardless).
  if (typeof canPurchase === 'boolean' && desc.control !== 'none') desc.enabled = canPurchase;
  const key = submissionKey(env);
  const busy = busyKey === key;
  const price = priceLabel(env);
  const testidBase = `product-${env.product_type}-${env.product_key}`;

  const priceRow = price != null ? (
    <div className="text-sm text-gray-900" data-testid={`${testidBase}-price`}>
      <span className="font-medium">{price}</span>
      <span className="text-gray-500">{cadenceLabel(env)}</span>
      <span className="block text-xs text-gray-400">{taxNote(env)}</span>
    </div>
  ) : null;

  // No enabled mutation control for non-purchaser roles — show state/price only (convenience hiding).
  const showPurchase = desc.control === 'purchase' && desc.enabled;
  const showRequest = desc.control === 'request_review' && desc.enabled;

  return (
    <div className="mt-2 space-y-1" data-testid={`${testidBase}-control`}>
      {priceRow}
      {quantityControl}

      {showPurchase && (
        <button type="button" data-testid={`${testidBase}-purchase-btn`} disabled={busy}
          onClick={() => onPurchase && onPurchase(env)}
          className="px-3 py-1 text-sm rounded bg-purple-600 text-white disabled:opacity-50">
          {busy ? 'Creating Checkout…' : actionLabel}
        </button>
      )}

      {showRequest && (
        <div>
          {desc.reason && <p className="text-xs text-gray-500" data-testid={`${testidBase}-request-reason`}>{desc.reason}</p>}
          <button type="button" data-testid={`${testidBase}-request-review-btn`} disabled={busy}
            onClick={() => onRequestReview && onRequestReview(env)}
            className="px-3 py-1 text-sm rounded border border-purple-300 text-purple-700 disabled:opacity-50">
            {busy ? 'Submitting…' : 'Request review'}
          </button>
        </div>
      )}

      {env.purchase_mode === PURCHASE_MODES.ALREADY_ACTIVE && (
        <span data-testid={`${testidBase}-already-active`} className="inline-block px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">Already active</span>
      )}
      {env.purchase_mode === PURCHASE_MODES.INCLUDED_IN_PLAN && (
        <span data-testid={`${testidBase}-included-in-plan`} className="inline-block px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">Included in plan</span>
      )}
      {env.purchase_mode === PURCHASE_MODES.INELIGIBLE && (
        <p data-testid={`${testidBase}-ineligible`} className="text-xs text-gray-500">{desc.reason}</p>
      )}
      {env.purchase_mode === PURCHASE_MODES.UNAVAILABLE && (
        <p data-testid={`${testidBase}-unavailable`} className="text-xs text-gray-500">{desc.reason}</p>
      )}

      {/* Non-purchaser role: state/price is visible above; a disabled-note communicates the control is admin-only. */}
      {(desc.control === 'purchase' || desc.control === 'request_review') && !desc.enabled && (
        <p className="text-xs text-gray-400" data-testid={`${testidBase}-admin-only`}>Only a Business Administrator can make this change.</p>
      )}
    </div>
  );
}
